import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import packageJson from '../../package.json';

// Query parameters whose VALUE browser telemetry must not record (lhphcapital-902l).
// Route tracking records the full address of every page, query string included — so the
// invite / password-reset link (/complete-account?token=, /reset-password?token=) was sent to
// App Insights as the previous page's URL. The SDK strikes only sig, Signature, AWSAccessKeyId
// and X-Goog-Signature by default; this list is ADDED to those (unlike the server's list,
// which replaces its default). Same names as api/src/utils/telemetryRedaction.js, plus token.
// ⚠️ Matched exactly and case-sensitively: `?Token=` is not struck.
export const REDACTED_QUERY_PARAMS = ['token', 'key', 'code', 'password'];

let reactPlugin: ReactPlugin | null = null;
let appInsights: ApplicationInsights | null = null;

/**
 * Determine the environment suffix for cloud role naming
 * Maps REACT_APP_ENV to the appropriate suffix: local, localdev, dev, prod
 */
const getEnvironmentSuffix = (): string => {
  const appEnv = process.env.REACT_APP_ENV || 'local';
  return appEnv;
};

/**
 * Initialize Application Insights for the React application
 * This should be called once at application startup
 */
export const initializeAppInsights = (): ReactPlugin | null => {
  console.log(
    'Initializing Application Insights...',
    process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING
  );
  const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

  if (!connectionString) {
    console.warn('Application Insights connection string not configured - telemetry disabled');
    return null;
  }

  try {
    // Create the React plugin for route tracking
    reactPlugin = new ReactPlugin();

    // Determine cloud role name based on environment
    const envSuffix = getEnvironmentSuffix();
    const cloudRoleName = `lhphai-app-${envSuffix}`;
    const cloudRoleInstance = 'browser';

    // Initialize Application Insights
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: true, // Automatically track route changes
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableCorsCorrelation: true,
        enableAjaxPerfTracking: true,
        disableFetchTracking: false,
        redactQueryParams: REDACTED_QUERY_PARAMS,
        extensions: [reactPlugin as any], // Type assertion to fix compatibility
      },
    });

    appInsights.loadAppInsights();

    // Set cloud role name and instance for Application Map
    appInsights.addTelemetryInitializer((envelope: any) => {
      if (envelope.tags) {
        envelope.tags['ai.cloud.role'] = cloudRoleName;
        envelope.tags['ai.cloud.roleInstance'] = cloudRoleInstance;
      }
      if (envelope.data) {
        envelope.data.app = 'lhph-frontend';
        envelope.data.version = packageJson.version;
        envelope.data.environment = envSuffix;
      }
    });

    console.log(`Application Insights initialized for frontend`);
    console.log(`  Cloud Role: ${cloudRoleName}`);
    console.log(`  Cloud Instance: ${cloudRoleInstance}`);
    return reactPlugin;
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
    return null;
  }
};

/**
 * Get the Application Insights instance
 */
export const getAppInsights = (): ApplicationInsights | null => {
  return appInsights;
};

/**
 * Get the React plugin instance
 */
export const getReactPlugin = (): ReactPlugin | null => {
  return reactPlugin;
};

/**
 * Track a custom event
 */
export const trackEvent = (name: string, properties?: { [key: string]: any }) => {
  if (appInsights) {
    appInsights.trackEvent({ name }, properties);
  }
};

/**
 * Track a custom metric
 */
export const trackMetric = (name: string, average: number, properties?: { [key: string]: any }) => {
  if (appInsights) {
    appInsights.trackMetric({ name, average }, properties);
  }
};

/**
 * Track an exception
 */
export const trackException = (error: Error, severityLevel?: number) => {
  if (appInsights) {
    appInsights.trackException({ exception: error, severityLevel });
  }
};

/**
 * Track a page view
 */
export const trackPageView = (name?: string, uri?: string) => {
  if (appInsights) {
    appInsights.trackPageView({ name, uri });
  }
};
